import { CreateElement } from "vue";
import { Component, Vue, Prop } from "vue-property-decorator";
import * as RecordRTC from "recordrtc";

// the component props:
// 1. recordID should be unique to what this componenet is
//    being used for.
// 2. callbackOnStop is an optional callback parameter in case the user wants to
//    know when the user stops recording, audio returns the audio in blob format.
//    Format can be changed upon request :).  Duration returns duration in seconds
//    of the clip

export enum RECORDING_STATE {
  STOP_RECORDING,
  START_RECORDING
}
@Component
export default class AudioHandler extends Vue {
  @Prop() disableStartButton!: boolean;
  @Prop() label!: { start: string; end: string };
  @Prop() enableAutoRec!: boolean;
  @Prop() allowOneUsage!: boolean;
  @Prop() showRecorder!: boolean;
  @Prop() recordID!: string;
  @Prop() autostartRecording!: boolean;
  @Prop() disabled!: boolean;
  @Prop() disableUserControl!: boolean;
  @Prop() recordingStartClick!: () => void;
  @Prop() parentInterference!: RECORDING_STATE;
  @Prop() callbackOnStop!: (
    audio: any,
    duration: number,
    blobAudio?: any
  ) => void;
  private _autoRecStarted!: boolean;
  private _microphone: any;
  private _startID!: string;
  private _audioControlID!: string;
  private _componentState!: {
    stopDisabled: boolean;
    startDisabled: boolean;
    stopUsage: boolean;
  };
  private _audio: any;
  private _recorder: any;

  // vue.js is weird and constructor calls do not work to set things up
  // so create things in created lifecycle
  created() {
    this._startID = this.recordID + "record-start";
    this._componentState = {
      stopDisabled: true,
      startDisabled: false,
      stopUsage: false
    };
    this._audioControlID = this.recordID + "record-audio-c";
    this._autoRecStarted = false;
  }

  beforeUpdate() {
    if (
      this._componentState.stopDisabled &&
      !this._autoRecStarted &&
      this.enableAutoRec
    ) {
      this._autoRecStarted = true;
      this._recordClicked();
    }
  }

  destroyed() {
    // this means the component is in use, so we need to destroy it and consume
    // the event without calling the parent
    if (!this._componentState.stopDisabled) {
      if (this._recorder) {
        this._recorder.destroy();
        this._recorder = null;
      }

      // release microphone
      if (this._microphone) {
        this._microphone.stop();
        this._microphone = null;
      }
    }
  }

  private _isEdge: any =
    navigator.userAgent.indexOf("Edge") !== -1 &&
    (!!navigator.msSaveOrOpenBlob || !!navigator.msSaveBlob);

  render(h: CreateElement) {
    let isRecordingNow = this._componentState.startDisabled
      ? "#F44336"
      : "inherit";
    return (
      <div>
        <v-row>
          <v-col>
            <v-btn
              disabled={
                this.disableStartButton ||
                (this._componentState.stopDisabled
                  ? (this._componentState.stopUsage || this.disabled) &&
                    !this._componentState.startDisabled
                  : true)
              }
              class="text-center"
              onClick={this._recordClicked}
              id={this._startID}
              style={{
                margin: `0 20px 10px 0`
              }}
            >
              <v-icon color={isRecordingNow}>mdi-record-circle</v-icon>
              <span
                style={{
                  marginLeft: "5px"
                }}
              >
                {this.label.start}
              </span>
            </v-btn>
            <v-btn
              disabled={
                this._componentState.stopUsage ||
                this.disabled ||
                this._componentState.stopDisabled
              }
              class="text-center"
              onClick={this._stopClicked}
              id={this._startID}
              style={{
                margin: `0 20px 10px 0`
              }}
            >
              <v-icon color={isRecordingNow}>mdi-stop-circle</v-icon>
              <span
                style={{
                  marginLeft: "5px",
                  color: isRecordingNow
                }}
              >
                {this.label.end}
              </span>
            </v-btn>
          </v-col>
        </v-row>
        {
          <v-col
            style={{
              display: this.disableUserControl ? "none" : "inherit"
            }}
            id={this._audioControlID}
          >
            <audio
              controlsList="nodownload"
              controls
              style={{
                margin: `10px 0 0 0`
              }}
            >
              <source src={this._audio} type="audio/webm" />
            </audio>
          </v-col>
        }
      </div>
    );
  }

  private async _stopClicked() {
    if (this._componentState.stopDisabled == true) return;
    this._componentState = {
      startDisabled: false,
      stopDisabled: true,
      stopUsage: this.allowOneUsage
    };
    await this._recorder.stopRecording();
    let blob = await this._recorder.getBlob();
    this._audio = URL.createObjectURL(blob);

    // append to the new child
    let audioControl: any = document.getElementById(this._audioControlID);
    audioControl.innerHTML = "";

    let newAudioPlayer = new Audio();
    newAudioPlayer.controls = true;
    newAudioPlayer.autoplay = false;
    newAudioPlayer.src = this._audio;
    audioControl.appendChild(newAudioPlayer);
    newAudioPlayer.addEventListener("loadedmetadata", () => {
      let executeTimeAvail = () => {
        newAudioPlayer.autoplay = !this.disableUserControl;
        newAudioPlayer.loop = true;
        this.$forceUpdate();
        if (this.callbackOnStop) {
          this.callbackOnStop(this._audio, newAudioPlayer.duration, blob);
        }
      };

      if (newAudioPlayer.duration === Infinity) {
        newAudioPlayer.currentTime = Number.MAX_SAFE_INTEGER;
        newAudioPlayer.ontimeupdate = () => {
          newAudioPlayer.ontimeupdate = null;
          executeTimeAvail();
          newAudioPlayer.currentTime = 0;
        };
      } else {
        executeTimeAvail();
      }
    });

    if (this._recorder) {
      this._recorder.destroy();
      this._recorder = null;
    }

    // release microphone
    if (this._microphone) {
      this._microphone.stop();
      this._microphone = null;
    }
  }

  private async _recordClicked() {
    if (this._componentState.startDisabled == true) return;

    if (this.recordingStartClick) {
      this.recordingStartClick();
    }

    const recorderOptions = {
      type: "audio",
      numberOfAudioChannels: this._isEdge ? 1 : 2,
      checkForInactiveTracks: true,
      bufferSize: 16384,
      disableLogs: true
    };

    this._microphone = await navigator.mediaDevices.getUserMedia({
      audio: true
    });

    this._recorder = new RecordRTC.RecordRTCPromisesHandler(
      this._microphone,
      recorderOptions
    );

    this._recorder.startRecording();
    this._componentState.startDisabled = true;
    this._componentState.stopDisabled = false;
    this._componentState.stopUsage = false;
    this.$forceUpdate();
  }

  private async _captureMicrophone(callback: any) {
    if (this._microphone) {
      callback(this._microphone);
      return;
    }

    if (
      typeof navigator.mediaDevices === "undefined" ||
      !navigator.mediaDevices.getUserMedia
    ) {
      alert("This browser does not support audio recording.");

      /* if (navigator.getUserMedia()) { */
      /* alert("This browser seems to support a deprecated audio recording."); */
      /* } */
    }

    navigator.mediaDevices
      .getUserMedia({
        audio: this._isEdge ? true : { echoCancellation: false }
      })
      .then(mic => {
        callback(mic);
      })
      .catch(err => {
        alert("Something went wrong. Reload the page, and try again");
        // console.error(err);
      });
  }
}

const component = AudioHandler;
